import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardBody, Badge } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface KPIs {
  users:      { total: number; active: number; brokers: number; admins: number };
  properties: { total: number; active: number; pending: number };
  auctions:   { live: number; scheduled: number; completed: number };
  requests:   { new: number; inProgress: number; closed: number };
  messages:   { new: number };
}

interface ActivityItem {
  type: string;
  icon: string;
  color: string;
  title: string;
  detail: string;
  refId: string;
  at: string;
}

export function AdminDashboard() {
  const { data: kpis } = useQuery({
    queryKey: ['admin', 'kpis'],
    queryFn: () => api.get<ApiSuccess<KPIs>>('/api/admin/kpis'),
    refetchInterval: 30_000,
  });

  const { data: activity } = useQuery({
    queryKey: ['admin', 'activity'],
    queryFn: () => api.get<ApiSuccess<{ hours: number; items: ActivityItem[] }>>('/api/admin/activity?hours=24'),
    refetchInterval: 60_000,
  });

  const k = kpis?.data;

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-2">لوحة الإدارة 👑</h1>
      <p className="text-gray-600 mb-6">إحصائيات حية + سجل النشاط (آخر 24 ساعة)</p>

      {/* 📊 KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KpiCard title="المستخدمون"   value={k?.users.total ?? 0}      icon="👥" hint={`${k?.users.active ?? 0} نشط`}      color="navy" link="/admin/users" />
        <KpiCard title="عقارات نشطة" value={k?.properties.active ?? 0} icon="🏘️" hint={`${k?.properties.pending ?? 0} قيد المراجعة`} color="green" link="/admin/properties" />
        <KpiCard title="مزادات مباشرة" value={k?.auctions.live ?? 0}    icon="🔨" hint={`${k?.auctions.scheduled ?? 0} قادم`}   color="amber" link="/admin/auctions" />
        <KpiCard title="طلبات جديدة"  value={k?.requests.new ?? 0}     icon="📋" hint={`${k?.requests.inProgress ?? 0} قيد المعالجة`} color="gold" link="/admin/requests" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <MiniStat label="مسوّقون"     value={k?.users.brokers ?? 0} />
        <MiniStat label="إداريون"     value={k?.users.admins ?? 0} />
        <MiniStat label="مزادات منتهية" value={k?.auctions.completed ?? 0} />
        <MiniStat label="رسائل جديدة" value={k?.messages.new ?? 0} link="/admin/messages" />
      </div>

      {/* 🔔 Activity */}
      <Card>
        <CardBody>
          <h2 className="font-black text-lg text-navy-dark mb-4">📊 سجل النشاط (آخر 24 ساعة)</h2>
          {!activity?.data?.items?.length ? (
            <p className="text-gray-500 text-center py-8">لا يوجد نشاط في آخر 24 ساعة</p>
          ) : (
            <div className="space-y-3">
              {activity.data.items.slice(0, 20).map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: `${item.color}22`, color: item.color }}>
                    {emojiFor(item.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-ink text-sm">{item.title}</div>
                    <div className="text-xs text-gray-500 truncate">{item.detail}</div>
                  </div>
                  <div className="text-xs text-gray-400 shrink-0">
                    {timeAgo(item.at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function KpiCard({ title, value, icon, hint, color, link }: { title: string; value: number; icon: string; hint?: string; color: 'navy' | 'green' | 'amber' | 'gold'; link?: string }) {
  const Inner = (
    <Card hover={!!link}>
      <CardBody>
        <div className="flex items-start justify-between mb-2">
          <div className="text-3xl">{icon}</div>
          <Badge color={color === 'amber' ? 'amber' : color === 'gold' ? 'gold' : color === 'green' ? 'green' : 'navy'}>
            {title}
          </Badge>
        </div>
        <div className="text-3xl font-black text-navy-dark">{value.toLocaleString('en-US')}</div>
        {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
      </CardBody>
    </Card>
  );
  return link ? <Link to={link}>{Inner}</Link> : Inner;
}

function MiniStat({ label, value, link }: { label: string; value: number; link?: string }) {
  const Inner = (
    <Card hover={!!link}>
      <CardBody className="text-center">
        <div className="text-2xl font-black text-navy-dark">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </CardBody>
    </Card>
  );
  return link ? <Link to={link}>{Inner}</Link> : Inner;
}

function emojiFor(type: string) {
  if (type === 'user') return '👤';
  if (type === 'property') return '🏠';
  if (type === 'auction') return '🔨';
  if (type === 'request') return '📋';
  if (type === 'message') return '✉️';
  if (type === 'bid') return '💰';
  return '✨';
}

function timeAgo(iso: string) {
  const diff = Date.now() - +new Date(iso);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'الآن';
  if (min < 60) return `قبل ${min} د`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `قبل ${hr} س`;
  return new Date(iso).toLocaleDateString('ar-SA');
}
